import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { CheckCircle2, ChevronRight, ShieldCheck, Copy, Loader2, Upload } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { createOrder, markPaid, uploadFileToStorage } from '../lib/api';

const Checkout = () => {
  const { cart, total, clearCart } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState('form'); // form -> pay -> done
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null); // { order_id, total, upi:{id,name,qr_image}}
  const [txn, setTxn] = useState('');
  const [payerName, setPayerName] = useState('');
  const [screenshot, setScreenshot] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [uploadingShot, setUploadingShot] = useState(false);
  const shotRef = React.useRef();

  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', pincode: '', notes: ''
  });

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address) {
      toast({ title: 'Missing info', description: 'Please fill required fields.' });
      return;
    }
    setSubmitting(true);
    try {
      const items = cart.map(i => ({
        id: i.id, title: i.title, brand: i.brand, image: i.image, sale: i.sale, qty: i.qty
      }));
      const res = await createOrder({ ...form, items });
      setOrder(res);
      setStep('pay');
      toast({ title: 'Order placed!', description: `Order #${res.order_id}` });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to place order';
      toast({ title: 'Error', description: String(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  const copyUPI = () => {
    if (order?.upi?.id) {
      navigator.clipboard.writeText(order.upi.id);
      toast({ title: 'Copied', description: 'UPI ID copied to clipboard.' });
    }
  };

  const confirmPayment = async () => {
    if (!txn.trim()) {
      toast({ title: 'Transaction ID required', description: 'Please enter your UPI transaction / UTR number.' });
      return;
    }
    if (!screenshot) {
      toast({ title: 'Screenshot required', description: 'Please upload your payment screenshot for verification.' });
      return;
    }
    setConfirming(true);
    try {
      await markPaid(order.id, { transaction_id: txn.trim(), payer_name: payerName.trim(), payment_screenshot: screenshot });
      clearCart();
      setStep('done');
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: err.message || 'Could not submit payment info.' });
    } finally {
      setConfirming(false);
    }
  };

  const onScreenshot = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast({ title: 'Too large', description: 'Screenshot must be under 5MB.' }); return; }
    
    setUploadingShot(true);
    try {
      const url = await uploadFileToStorage(f, 'payments');
      setScreenshot(url);
      toast({ title: 'Uploaded!', description: 'Payment screenshot uploaded successfully.' });
    } catch (err) {
      console.error('Screenshot upload error:', err);
      toast({ title: 'Upload failed', description: 'Failed to upload screenshot to Firebase Storage.', variant: 'destructive' });
    } finally {
      setUploadingShot(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center bg-white">
        <CheckCircle2 size={70} className="mx-auto text-green-600 mb-5"/>
        <h1 className="font-serif text-3xl mb-3">Payment Submitted!</h1>
        <p className="text-neutral-600 mb-2">Order ID: <span className="font-semibold text-[#1a1a1a]">#{order.order_id}</span></p>
        <p className="text-neutral-600 mb-6 max-w-md mx-auto">Our team will verify your payment and confirm your order within 24 hours. A confirmation has been sent to your email.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/"><Button className="bg-[#b8892d] hover:bg-[#a07826] h-11 px-6 uppercase tracking-widest text-xs">Back to Home</Button></Link>
          <Link to="/shop"><Button variant="outline" className="h-11 px-6 uppercase tracking-widest text-xs">Continue Shopping</Button></Link>
        </div>
      </div>
    );
  }

  if (step === 'pay' && order) {
    return (
      <div className="bg-white min-h-[70vh]">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="flex items-center gap-2 text-xs text-neutral-500 mb-4">
            <Link to="/" className="hover:text-[#b8892d]">Home</Link>
            <ChevronRight size={12}/>
            <span className="text-neutral-800">Payment</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl mb-2">Complete Your Payment</h1>
          <p className="text-neutral-600 mb-8">Order <span className="font-medium text-[#1a1a1a]">#{order.order_id}</span> · Amount to pay: <span className="font-semibold text-[#b8892d]">₹{Number(order.total).toLocaleString('en-IN')}</span></p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* QR / UPI */}
            <div className="border border-neutral-200 p-6 text-center bg-neutral-50">
              <div className="text-xs tracking-widest uppercase text-[#b8892d] mb-3">Scan &amp; Pay</div>
              {order.upi?.qr_image ? (
                <img src={order.upi.qr_image} alt="UPI QR" className="w-56 h-56 object-contain mx-auto bg-white border border-neutral-200 p-2"/>
              ) : (
                <div className="w-56 h-56 mx-auto flex items-center justify-center border border-dashed border-neutral-300 bg-white text-neutral-400 text-sm text-center px-4">
                  QR not uploaded yet. Please use UPI ID on the right.
                </div>
              )}
              <div className="mt-4 text-xs text-neutral-500">Compatible with GPay, PhonePe, Paytm, BHIM &amp; all UPI apps</div>
            </div>

            {/* UPI ID + confirm */}
            <div className="border border-neutral-200 p-6 space-y-4 bg-white">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#b8892d]">UPI ID</Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 border border-neutral-300 px-3 py-2.5 font-mono text-sm bg-neutral-50 break-all">{order.upi?.id || 'Not configured'}</div>
                  <button onClick={copyUPI} className="p-2.5 border border-neutral-300 hover:border-[#b8892d] hover:text-[#b8892d]"><Copy size={16}/></button>
                </div>
                {order.upi?.name && <div className="text-xs text-neutral-500 mt-1">Merchant: {order.upi.name}</div>}
              </div>
              <div className="pt-2 border-t border-neutral-200">
                <Label>UPI Transaction ID / UTR *</Label>
                <Input required value={txn} onChange={e => setTxn(e.target.value)} placeholder="e.g. 405812345678" className="mt-1"/>
              </div>
              <div>
                <Label>Payer Name (optional)</Label>
                <Input value={payerName} onChange={e => setPayerName(e.target.value)} placeholder="Name on UPI app" className="mt-1"/>
              </div>
              <div>
                <Label>Payment Screenshot *</Label>
                <input ref={shotRef} type="file" accept="image/*" onChange={onScreenshot} className="hidden" disabled={uploadingShot}/>
                <div className="mt-1 flex items-start gap-3">
                  <div className="w-20 h-20 border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {uploadingShot ? (
                      <Loader2 size={20} className="animate-spin text-neutral-400" />
                    ) : screenshot ? (
                      <img src={screenshot} alt="Screenshot" className="w-full h-full object-cover"/>
                    ) : (
                      <span className="text-[10px] text-neutral-400 text-center px-1">No image</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <Button type="button" variant="outline" onClick={() => shotRef.current?.click()} className="gap-2 h-9 w-full" disabled={uploadingShot}>
                      {uploadingShot ? (
                        <Loader2 size={14} className="animate-spin"/>
                      ) : (
                        <Upload size={14}/>
                      )}
                      {uploadingShot ? 'Uploading...' : screenshot ? 'Change Screenshot' : 'Upload Screenshot'}
                    </Button>
                    <p className="text-[11px] text-neutral-500 mt-1">Upload proof of payment (JPG/PNG, max 3MB)</p>
                  </div>
                </div>
              </div>
              <Button disabled={confirming || uploadingShot} onClick={confirmPayment} className="w-full bg-[#b8892d] hover:bg-[#a07826] h-11 uppercase tracking-widest text-xs">
                {confirming ? <><Loader2 size={16} className="animate-spin mr-2"/> Submitting...</> : 'I have paid'}
              </Button>
              <div className="flex items-start gap-2 text-xs text-neutral-500">
                <ShieldCheck size={14} className="text-[#b8892d] flex-shrink-0 mt-0.5"/>
                <span>After you submit the transaction ID, our team will verify the payment and confirm your order within 24 hours.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step: form
  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <p className="text-neutral-600 mb-4">Your basket is empty.</p>
        <Link to="/shop"><Button className="bg-[#b8892d] hover:bg-[#a07826]">Continue Shopping</Button></Link>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-xs text-neutral-500 mb-4">
          <Link to="/" className="hover:text-[#b8892d]">Home</Link>
          <ChevronRight size={12}/>
          <span className="text-neutral-800">Checkout</span>
        </div>
        <h1 className="font-serif text-3xl md:text-4xl mb-8">Checkout</h1>
        <form onSubmit={submitOrder} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="border border-neutral-200 p-6 space-y-4">
              <h3 className="font-semibold text-lg">Billing Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Full Name *</Label><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1"/></div>
                <div><Label>Phone *</Label><Input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1"/></div>
                <div className="md:col-span-2"><Label>Email *</Label><Input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1"/></div>
                <div className="md:col-span-2"><Label>Address *</Label><Input required value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="mt-1"/></div>
                <div><Label>City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="mt-1"/></div>
                <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => setForm({...form, pincode: e.target.value})} className="mt-1"/></div>
                <div className="md:col-span-2"><Label>Order Notes (optional)</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1" rows={2}/></div>
              </div>
            </div>
            <div className="border border-neutral-200 p-6 bg-[#fff8e1]">
              <h3 className="font-semibold text-lg mb-2">Payment Method</h3>
              <p className="text-sm text-neutral-700">UPI / Scanner Payment — On next screen you'll see a QR code &amp; UPI ID to pay the full amount. Order will be confirmed after payment verification.</p>
            </div>
          </div>
          <div className="bg-neutral-50 p-6 h-fit sticky top-32">
            <h3 className="font-serif text-xl mb-4">Your Order</h3>
            <div className="space-y-3 text-sm mb-4">
              {cart.map(i => (
                <div key={i.id} className="flex justify-between gap-2">
                  <span className="text-neutral-700 flex-1 line-clamp-1">{i.title} × {i.qty}</span>
                  <span>₹{(i.sale * i.qty).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2 text-sm pt-3 border-t border-neutral-200">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{total.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span className="text-green-700">Free</span></div>
              <div className="flex justify-between font-semibold text-base pt-3 border-t border-neutral-200"><span>Total</span><span className="text-[#b8892d] text-lg">₹{total.toLocaleString('en-IN')}</span></div>
            </div>
            <Button disabled={submitting} type="submit" className="w-full mt-5 bg-[#b8892d] hover:bg-[#a07826] h-11 uppercase tracking-widest text-xs">
              {submitting ? <><Loader2 size={16} className="animate-spin mr-2"/> Placing Order...</> : 'Place Order & Pay'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
